import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, ChevronDown, ChevronUp, Loader2, Pencil, Save, Trash2, X } from 'lucide-react';
import { CenteredLoader } from '../CenteredLoader';
import { NoProjectSelectedState } from '../NoProjectSelectedState';
import { PageErrorBanner } from '../PageErrorBanner';
import SearchableSelect from '../SearchableSelect';
import { RuleSwitch } from '../red-flag-rules/RuleSwitch';
import PreJourneyBaseCitiesModal from './PreJourneyBaseCitiesModal';
import PreJourneyGroupValuesModal from './PreJourneyGroupValuesModal';
import PreJourneyPriorityModal, { type PriorityDraftResult } from './PreJourneyPriorityModal';
import {
  GROUP_NAME_MAX,
  MAX_GROUPS,
  MAX_GROUP_FIELDS,
  MAX_PRIORITIES,
  fieldLabel,
  fieldMapOf,
  groupValuesLine,
  nextId,
  prioritySummary,
  regroup,
  usesLocation,
  visitsLabel,
} from './preJourneyPlanConfig';
import { preJourneyPlanAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import type {
  PlanConfig,
  PlanFieldsResponse,
  PlanGroupConfig,
  PlanPriorityConfig,
  PlanSettingsResponse,
  StoreFieldKey,
} from '../../types/preJourneyPlan';

/**
 * Master Data → Store Visits Configuration → Pre-Journey Plan.
 *
 * Which roles get a monthly visit plan, and the plan's config. A planned person's stores (the
 * ones they handle in Store Visits) fall in store groups, made from up to two store fields
 * (Location vs base city, Trade, a custom field, …): one group per value, which can be renamed,
 * merged with other values or switched off. A store in no group, or in a group switched off, is
 * not planned. Inside its group a store gets one priority, first match wins; a priority is a
 * name, conditions on the store or the people deployed there, and its visits. A priority
 * switched off, or asking no visits this month, is skipped: the store falls through to the next.
 *
 * Nothing ticked is the plan switched off. One Save sends the roles and the whole config; the
 * server then rebuilds this month's plan (past months keep the plan they were built with).
 * Base cities are reviewed in their own window (PreJourneyBaseCitiesModal), saved on their own,
 * and only offered while the config reads the location.
 */

/** Asked by the page before a tab switch leaves unsaved edits behind. */
export const DISCARD_PLAN_PROMPT = 'Discard your unsaved changes to the Pre-Journey Plan?';
/** Asked before a new "Group stores by" drops priorities no new group can keep. */
export const DISCARD_PRIORITIES_PROMPT = 'Changing the grouping drops the priorities set for some groups. Go on?';

interface PreJourneyPlanTabProps {
  /** Where the page's tab row wants this tab's Save button. */
  actionSlot: HTMLElement | null;
  /** Told whenever the tab gains or loses unsaved edits, so leaving the tab can ask first. */
  onDirtyChange?: (dirty: boolean) => void;
}

const NAME_ERROR = 'Enter a name.';
const LOAD_FAILED = 'Could not load the Pre-Journey Plan.';
const SAVE_FAILED = 'Could not save the Pre-Journey Plan.';

const apiMessage = (e: unknown, fallback: string): string => {
  const message = (e as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

interface Draft {
  roleIds: string[];
  config: PlanConfig;
}
/** A group name refused on Save, by group id. */
type NameErrors = Record<string, string>;

const isConfig = (c: unknown): c is PlanConfig =>
  Boolean(c) && Array.isArray((c as PlanConfig).groupBy) && Array.isArray((c as PlanConfig).groups);

/** The saved settings as a draft: roles the project no longer has are dropped. */
const toDraft = (data: PlanSettingsResponse): Draft => {
  const known = new Set(data.roles.map((r) => r.id));
  const config = JSON.parse(JSON.stringify(data.settings.config)) as PlanConfig;
  return {
    roleIds: (data.settings.roleIds ?? []).filter((id) => known.has(id)),
    config: {
      schema: 2,
      groupBy: config.groupBy,
      groups: config.groups.map((g) => ({ ...g, match: g.match ?? [], priorities: g.priorities ?? [] })),
    },
  };
};

/** The config as Save sends it: names as they will be stored. */
const toSend = (config: PlanConfig): PlanConfig => ({
  schema: 2,
  groupBy: config.groupBy,
  groups: config.groups.map((g) => ({ ...g, name: g.name.trim() })),
});

/** What Save would send, as a string: two drafts with one fingerprint have nothing to save between them. */
const fingerprint = (d: Draft): string => JSON.stringify([[...d.roleIds].sort(), toSend(d.config)]);

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-11' → 'November 2026'. */
const monthLabel = (ym: string): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return ym;
  return `${MONTH_NAMES[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
};

/** A moment as India reads it: '1 Nov, 12:30 am'. Worked out from UTC, so the reader's own time zone cannot move it. */
const indiaTime = (iso: string): string | null => {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms + 330 * 60 * 1000);
  const hours = d.getUTCHours();
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${d.getUTCDate()} ${SHORT_MONTHS[d.getUTCMonth()]}, ${h12}:${minutes} ${hours < 12 ? 'am' : 'pm'}`;
};


const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

const CARD = 'rounded-lg border border-gray-100 bg-white p-6';
const LABEL = 'text-sm font-semibold text-gray-900';
const LINK = 'text-sm font-medium text-primary-600 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-50';
const ICON_BUTTON = 'rounded p-1 text-gray-400 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-30';

/** The priority window: for a group, a priority being edited, or a new one (priorityId null). */
type PriorityWindow = { groupId: string; priorityId: string | null };
/** The values window: merge into a group, or add a new group (groupId null). */
type ValuesWindow = { groupId: string | null };

export default function PreJourneyPlanTab({ actionSlot, onDirtyChange }: PreJourneyPlanTabProps) {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);
  const uid = useId();

  // The project whose plan is on screen; it trails a project switch by one render, so an
  // answer still on its way for the old project can be told apart.
  const [projectId, setProjectId] = useState(selectedProjectId);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The server's answer (roles, month, build time, the two warnings) and the settings as saved and as edited.
  const [info, setInfo] = useState<PlanSettingsResponse | null>(null);
  const [fields, setFields] = useState<PlanFieldsResponse | null>(null);
  const [baseline, setBaseline] = useState<Draft | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<NameErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showOver, setShowOver] = useState(false);
  const [citiesOpen, setCitiesOpen] = useState(false);
  const [addingSecond, setAddingSecond] = useState(false);
  // Set when the fields just picked have more values than groups allowed, so none was made.
  const [tooMany, setTooMany] = useState<number | null>(null);
  const [priorityWindow, setPriorityWindow] = useState<PriorityWindow | null>(null);
  const [valuesWindow, setValuesWindow] = useState<ValuesWindow | null>(null);
  const [refusals, setRefusals] = useState(0);
  const rulesRef = useRef<HTMLDivElement>(null);
  const shownProject = useRef(projectId);
  shownProject.current = projectId;

  const fieldMap = useMemo(() => fieldMapOf(fields ? [fields.store, fields.people] : []), [fields]);
  const dirty = baseline !== null && draft !== null && fingerprint(draft) !== fingerprint(baseline);

  useEffect(() => {
    setProjectId(selectedProjectId);
  }, [selectedProjectId]);

  const applyAnswer = (data: PlanSettingsResponse) => {
    const next = toDraft(data);
    setInfo(data);
    setBaseline(next);
    setDraft(next);
    setErrors({});
    setTooMany(null);
    setAddingSecond(false);
  };

  useEffect(() => {
    setInfo(null);
    setFields(null);
    setBaseline(null);
    setDraft(null);
    setErrors({});
    setLoadError(null);
    setSaveError(null);
    setNotice(null);
    setShowOver(false);
    setCitiesOpen(false);
    setPriorityWindow(null);
    setValuesWindow(null);
    if (!projectId) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    Promise.all([preJourneyPlanAPI.getSettings(projectId), preJourneyPlanAPI.getFields(projectId)])
      .then(([settingsRes, fieldsRes]) => {
        if (!alive) return;
        const data = settingsRes.data?.data as PlanSettingsResponse | undefined;
        const got = fieldsRes.data?.data as PlanFieldsResponse | undefined;
        if (!data || !data.settings || !Array.isArray(data.roles) || !isConfig(data.settings.config)
          || !got || !Array.isArray(got.store) || !Array.isArray(got.people)) {
          setLoadError(LOAD_FAILED);
          return;
        }
        setFields(got);
        applyAnswer(data);
      })
      .catch((e) => alive && setLoadError(apiMessage(e, LOAD_FAILED)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [projectId]);

  // Leaving the page (reload / close tab) with unsaved edits asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (refusals > 0) rulesRef.current?.querySelector<HTMLInputElement>('input[aria-invalid="true"]')?.focus();
  }, [refusals]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const toggleRole = (roleId: string) => {
    setNotice(null);
    setDraft((d) => (d ? {
      ...d,
      roleIds: d.roleIds.includes(roleId) ? d.roleIds.filter((id) => id !== roleId) : [...d.roleIds, roleId],
    } : d));
  };

  const editConfig = (change: (config: PlanConfig) => PlanConfig) => {
    setNotice(null);
    setDraft((d) => (d ? { ...d, config: change(d.config) } : d));
  };
  const editGroup = (groupId: string, change: (group: PlanGroupConfig) => PlanGroupConfig) =>
    editConfig((c) => ({ ...c, groups: c.groups.map((g) => (g.id === groupId ? change(g) : g)) }));
  const editPriority = (groupId: string, priorityId: string, change: Partial<PlanPriorityConfig>) =>
    editGroup(groupId, (g) => ({ ...g, priorities: g.priorities.map((p) => (p.id === priorityId ? { ...p, ...change } : p)) }));

  const renameGroup = (groupId: string, name: string) => {
    editGroup(groupId, (g) => ({ ...g, name }));
    // A refusal belongs to what the box held; it is checked again on Save.
    setErrors((prev) => {
      if (!prev[groupId]) return prev;
      const next = { ...prev };
      delete next[groupId];
      return next;
    });
  };

  /** A new "Group stores by": the groups are made again from the fields' values. */
  const changeGroupBy = (next: StoreFieldKey[]) => {
    if (!draft) return;
    setAddingSecond(false);
    if (JSON.stringify(next) === JSON.stringify(draft.config.groupBy)) return;
    const result = regroup(draft.config, next, fieldMap);
    if (result.lost.length > 0 && !window.confirm(DISCARD_PRIORITIES_PROMPT)) return;
    setNotice(null);
    setDraft({ ...draft, config: { schema: 2, groupBy: next, groups: result.groups } });
    setTooMany(result.tooMany);
    setErrors({});
  };

  const movePriority = (groupId: string, index: number, by: -1 | 1) => editGroup(groupId, (g) => {
    const list = [...g.priorities];
    const to = index + by;
    if (to < 0 || to >= list.length) return g;
    [list[index], list[to]] = [list[to], list[index]];
    return { ...g, priorities: list };
  });

  const deletePriority = (groupId: string, priorityId: string) =>
    editGroup(groupId, (g) => ({ ...g, priorities: g.priorities.filter((p) => p.id !== priorityId) }));

  const putPriority = (result: PriorityDraftResult) => {
    const open = priorityWindow;
    if (!open) return;
    if (open.priorityId) {
      editPriority(open.groupId, open.priorityId, result);
    } else {
      editGroup(open.groupId, (g) => ({
        ...g,
        priorities: [...g.priorities, { id: nextId('p', g.priorities.map((p) => p.id)), enabled: true, ...result }],
      }));
    }
    setPriorityWindow(null);
  };

  /** After a base-city save: the plan was rebuilt, so the build time and the two warnings are read again. Edits on screen stay. */
  const refreshInfo = async () => {
    if (!projectId) return;
    try {
      const res = await preJourneyPlanAPI.getSettings(projectId);
      const data = res.data?.data as PlanSettingsResponse | undefined;
      if (shownProject.current !== projectId || !data || !data.settings) return;
      setInfo(data);
    } catch {
      // The figures stay as they were; the next visit to the tab reads them again.
    }
  };

  const save = async () => {
    if (!projectId || !dirty || saving || !draft || !baseline) return;
    const found: NameErrors = {};
    for (const g of draft.config.groups) if (g.name.trim() === '') found[g.id] = NAME_ERROR;
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setRefusals((n) => n + 1);
      return;
    }

    setSaving(true);
    setSaveError(null);
    setNotice(null);
    try {
      // In the project's role order, not the order they were ticked, so the same choice always reads the same.
      const roleIds = (info?.roles ?? []).map((r) => r.id).filter((id) => draft.roleIds.includes(id));
      const res = await preJourneyPlanAPI.saveSettings(projectId, { roleIds, config: toSend(draft.config) });
      if (shownProject.current !== projectId) return;
      const data = res.data?.data as PlanSettingsResponse | undefined;
      if (data && data.settings && Array.isArray(data.roles) && isConfig(data.settings.config)) applyAnswer(data);
      else setBaseline(draft);
      // When the rebuild failed the save still stands; the warning line says the rest.
      setNotice(draft.roleIds.length === 0 ? 'Saved. The plan is off for this project.'
        : data?.buildError ? 'Saved.' : "Saved. This month's plan was rebuilt.");
    } catch (e) {
      if (shownProject.current === projectId) setSaveError(apiMessage(e, SAVE_FAILED));
    } finally {
      setSaving(false);
    }
  };

  if (!selectedProjectId) {
    return <NoProjectSelectedState message="Select a project from the dropdown in the menu bar." />;
  }
  if (loading) return <CenteredLoader />;
  if (loadError || !info || !draft || !fields) return <PageErrorBanner message={loadError ?? LOAD_FAILED} />;

  const built = info.builtAt ? indiaTime(info.builtAt) : null;
  const missing = info.missingBaseCity ?? 0;
  const over = info.overCapacity ?? [];
  const { groupBy, groups } = draft.config;

  // The store fields stores can be grouped by; a saved one the project no longer offers stays readable.
  const groupable = fields.store.filter((f) => f.groupable);
  const groupOptions = (other: StoreFieldKey | undefined) => {
    const options = groupable.filter((f) => f.key !== other).map((f) => ({ value: f.key, label: f.label }));
    for (const key of groupBy) {
      if (key !== other && !options.some((o) => o.value === key)) options.push({ value: key, label: fieldLabel(fieldMap, key) });
    }
    return options;
  };
  const showSecond = groupBy.length === MAX_GROUP_FIELDS || addingSecond;

  const priorityRow = (g: PlanGroupConfig, p: PlanPriorityConfig, index: number) => {
    const summary = prioritySummary(p, fieldMap);
    // A priority switched off, or in a group switched off, is drawn dimmed; its controls still work.
    const dim = !p.enabled || !g.enabled;
    const label = `${p.name}, ${g.name.trim() || 'store group'}`;
    return (
      <li key={p.id} className="py-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <RuleSwitch
            checked={p.enabled}
            onChange={(next) => editPriority(g.id, p.id, { enabled: next })}
            label={label}
            disabled={saving}
          />
          <div className="min-w-[9rem] flex-1">
            <div className={`truncate text-sm ${dim ? 'text-gray-400' : 'text-gray-900'}`}>{`${index + 1} · ${p.name}`}</div>
            <div className="truncate text-xs text-gray-500" title={summary.full}>{summary.short}</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className={`whitespace-nowrap rounded-lg border border-gray-300 px-2 py-1 text-sm tabular-nums ${dim ? 'text-gray-400' : 'text-gray-900'}`}>
              {visitsLabel(p.visits)}
            </span>
            <div className="flex items-center">
              <button
                type="button"
                aria-label={`Move ${p.name} up`}
                disabled={saving || index === 0}
                onClick={() => movePriority(g.id, index, -1)}
                className={ICON_BUTTON}
              >
                <ChevronUp className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                aria-label={`Move ${p.name} down`}
                disabled={saving || index === g.priorities.length - 1}
                onClick={() => movePriority(g.id, index, 1)}
                className={ICON_BUTTON}
              >
                <ChevronDown className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                aria-label={`Edit ${p.name}`}
                disabled={saving}
                onClick={() => setPriorityWindow({ groupId: g.id, priorityId: p.id })}
                className={ICON_BUTTON}
              >
                <Pencil className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                aria-label={`Delete ${p.name}`}
                disabled={saving}
                onClick={() => deletePriority(g.id, p.id)}
                className={`${ICON_BUTTON} hover:text-red-600`}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </li>
    );
  };

  const groupCard = (g: PlanGroupConfig) => {
    const title = g.name.trim() || 'Store group';
    const error = errors[g.id];
    const errorId = `${uid}-${g.id}-name-error`;
    const line = groupValuesLine(groupBy, g);
    return (
      <section key={g.id} className={CARD} aria-label={title}>
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <input
              type="text"
              aria-label="Group name"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
              maxLength={GROUP_NAME_MAX}
              value={g.name}
              disabled={saving}
              onChange={(e) => renameGroup(g.id, e.target.value)}
              className={`-ml-1 w-full rounded border bg-transparent px-1 py-0.5 text-sm font-semibold focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                error ? 'border-red-400' : 'border-transparent hover:border-gray-300'
              } ${g.enabled ? 'text-gray-900' : 'text-gray-400'}`}
            />
            {error && <p id={errorId} className="mt-1 text-xs text-red-700">{error}</p>}
            <p className="mt-1 flex min-w-0 items-center gap-1 text-xs text-gray-500">
              <span className="truncate" title={line.full}>{line.short}</span>
              {groupBy.length > 0 && (
                <>
                  <span aria-hidden>·</span>
                  <button
                    type="button"
                    aria-label={`Edit values of ${title}`}
                    disabled={saving}
                    onClick={() => setValuesWindow({ groupId: g.id })}
                    className="flex-shrink-0 font-medium text-primary-600 hover:text-primary-700 hover:underline disabled:opacity-50"
                  >
                    Edit values
                  </button>
                </>
              )}
            </p>
          </div>
          <RuleSwitch
            checked={g.enabled}
            onChange={(next) => editGroup(g.id, (x) => ({ ...x, enabled: next }))}
            label={g.enabled ? 'Group on' : 'Group off'}
            title={g.enabled ? 'Group on' : 'Group off'}
            disabled={saving}
          />
        </div>
        {g.priorities.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">No priorities yet.</p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100">
            {g.priorities.map((p, i) => priorityRow(g, p, i))}
          </ul>
        )}
        <button
          type="button"
          disabled={saving || g.priorities.length >= MAX_PRIORITIES}
          onClick={() => setPriorityWindow({ groupId: g.id, priorityId: null })}
          className={`mt-3 ${LINK}`}
        >
          + Add priority
        </button>
      </section>
    );
  };

  const windowGroup = priorityWindow ? groups.find((g) => g.id === priorityWindow.groupId) : undefined;

  return (
    <div className="space-y-6">
      {actionSlot && createPortal(
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}
          Save
        </button>,
        actionSlot,
      )}

      {notice && (
        <div role="status" className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <CheckCircle className="h-4 w-4 flex-shrink-0" aria-hidden />
          {notice}
        </div>
      )}
      {saveError && <PageErrorBanner message={saveError} />}

      <section className={CARD} aria-label="Who gets a plan">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className={LABEL}>Plan is on for</h2>
            {info.roles.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">This project has no roles yet.</p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {info.roles.map((role) => {
                  const on = draft.roleIds.includes(role.id);
                  return (
                    <button
                      key={role.id}
                      type="button"
                      aria-pressed={on}
                      disabled={saving}
                      onClick={() => toggleRole(role.id)}
                      className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 ${
                        on ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-primary-200'
                      }`}
                    >
                      {role.name}
                    </button>
                  );
                })}
              </div>
            )}
            {draft.roleIds.length === 0 && info.roles.length > 0 && (
              <p className="mt-2 text-xs text-gray-500">Nothing ticked: the plan is off.</p>
            )}
          </div>
          <p className="text-sm text-gray-600">
            Plan for <span className="font-medium text-gray-900">{monthLabel(info.planMonth)}</span>
            {' · '}
            {built ? `built ${built}` : 'not built yet'}
          </p>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <label htmlFor={`${uid}-by-0`} className={LABEL}>Group stores by</label>
          <div className="w-56">
            <SearchableSelect
              id={`${uid}-by-0`}
              value={groupBy[0] ?? ''}
              onChange={(key) => changeGroupBy(groupBy.length > 1 ? [key as StoreFieldKey, groupBy[1]] : [key as StoreFieldKey])}
              options={groupOptions(groupBy[1])}
              placeholder="Pick a field"
              allowClear={false}
              disabled={saving}
            />
          </div>
          {groupBy[0] && (
            <button
              type="button"
              aria-label={`Remove ${fieldLabel(fieldMap, groupBy[0])}`}
              disabled={saving}
              onClick={() => changeGroupBy(groupBy.slice(1))}
              className={ICON_BUTTON}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
          {groupBy.length > 0 && showSecond && (
            <>
              <span className="text-sm text-gray-500">and</span>
              <label htmlFor={`${uid}-by-1`} className="sr-only">Second field</label>
              <div className="w-56">
                <SearchableSelect
                  id={`${uid}-by-1`}
                  value={groupBy[1] ?? ''}
                  onChange={(key) => changeGroupBy([groupBy[0], key as StoreFieldKey])}
                  options={groupOptions(groupBy[0])}
                  placeholder="Pick a field"
                  allowClear={false}
                  disabled={saving}
                />
              </div>
              <button
                type="button"
                aria-label={groupBy[1] ? `Remove ${fieldLabel(fieldMap, groupBy[1])}` : 'Remove second field'}
                disabled={saving}
                onClick={() => (groupBy[1] ? changeGroupBy([groupBy[0]]) : setAddingSecond(false))}
                className={ICON_BUTTON}
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </>
          )}
          {groupBy.length === 1 && !showSecond && (
            <button type="button" disabled={saving} onClick={() => setAddingSecond(true)} className={LINK}>
              + second field
            </button>
          )}
        </div>
        {tooMany !== null && (
          <p className="mt-2 text-xs text-amber-700">
            {`${tooMany.toLocaleString()} groups needed, ${MAX_GROUPS} at most. Add groups below.`}
          </p>
        )}

        {usesLocation(draft.config) && (
          <p className="mt-4 text-sm text-gray-600">
            Base city of each AM: from their 1Office position.{' '}
            {missing > 0 ? `${missing.toLocaleString()} ${plural(missing, 'has', 'have')} none.` : 'Everyone on the plan has one.'}{' '}
            <button
              type="button"
              onClick={() => setCitiesOpen(true)}
              className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
            >
              Review base cities
            </button>
          </p>
        )}
      </section>

      <div ref={rulesRef} className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {groups.map(groupCard)}
      </div>

      {groupBy.length > 0 && (
        <p className="text-sm text-gray-600">
          Stores in no group are not planned
          {' · '}
          <button
            type="button"
            disabled={saving || groups.length >= MAX_GROUPS}
            onClick={() => setValuesWindow({ groupId: null })}
            className={LINK}
          >
            + Add store group
          </button>
        </p>
      )}

      {info.buildError && (
        <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {info.buildError}
        </p>
      )}

      {over.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p>
            {over.length.toLocaleString()} {plural(over.length, 'person', 'people')} would need more visits this month than their visit targets allow.{' '}
            <button
              type="button"
              aria-expanded={showOver}
              onClick={() => setShowOver((v) => !v)}
              className="font-medium underline hover:text-amber-900"
            >
              {showOver ? 'Hide' : 'See who'}
            </button>
          </p>
          {showOver && (
            <ul className="mt-2 space-y-1">
              {over.map((p) => (
                <li key={p.employeeCode}>
                  {p.name || p.employeeCode} ({p.employeeCode}) · {p.due.toLocaleString()} due · target allows {p.capacity.toLocaleString()}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <p className="text-xs text-gray-500">Saving rebuilds this month&apos;s plan. Past months keep the plan they had.</p>

      {priorityWindow && windowGroup && (
        <PreJourneyPriorityModal
          initial={priorityWindow.priorityId ? windowGroup.priorities.find((p) => p.id === priorityWindow.priorityId) : undefined}
          fields={fields}
          onClose={() => setPriorityWindow(null)}
          onDone={putPriority}
        />
      )}

      {valuesWindow && (
        <PreJourneyGroupValuesModal
          group={valuesWindow.groupId ? groups.find((g) => g.id === valuesWindow.groupId) : undefined}
          groupBy={groupBy}
          groups={groups}
          fields={fieldMap}
          onClose={() => setValuesWindow(null)}
          onApply={(next) => {
            editConfig((c) => ({ ...c, groups: next }));
            setValuesWindow(null);
          }}
        />
      )}

      {citiesOpen && projectId && (
        <PreJourneyBaseCitiesModal
          projectId={projectId}
          onClose={() => setCitiesOpen(false)}
          onSaved={(_changed, buildError) => {
            setCitiesOpen(false);
            setNotice(buildError ? 'Base cities saved.' : "Base cities saved. This month's plan was rebuilt.");
            void refreshInfo();
          }}
        />
      )}
    </div>
  );
}
